import type { UseMutationResult } from '@tanstack/react-query';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { syncUser } from '@features/users/api/sync-user/syncUser';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

/**
 * Mutation hook for syncing user data from Salesforce to CAE Connect.
 * On error, emits a danger flag with a "Try again" action.
 * On success, calls the provided onSuccess callback.
 */
export function useSyncUser(
  userId: string,
  onSuccess: () => void
): UseMutationResult<void, Error, void> {
  const handleSync = useCallback((): Promise<void> => {
    return syncUser(userId);
  }, [userId]);

  const mutateRef = useRef<() => void>(() => {});

  const mutation = useMutation<void, Error, void>({
    mutationFn: handleSync,
    onSuccess,
    onError: (): void => {
      emitFlagMessage(
        'danger',
        'Updating user information failed.',
        5000,
        true,
        'Try again',
        (): void => {
          mutateRef.current();
        }
      );
    },
  });

  mutateRef.current = mutation.mutate;

  return mutation;
}
